import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import SearchableAthleteSelect from '../components/SearchableAthleteSelect';
import TargetAudienceSelector from '../components/TargetAudienceSelector';
import PostMatchWizard from '../components/PostMatchWizard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StaffMatches = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [tagMap, setTagMap] = useState({});  // { athlete_id: [tag_id, ...] } — resolves target_tag_ids
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [performanceDialogOpen, setPerformanceDialogOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);

  const [newMatch, setNewMatch] = useState({
    date: '',
    opponent: '',
    competition: '',
    location: '',
    result: '',
    target_type: 'all',
    target_squads: [],
    target_athletes: [],
  });

  const [performanceData, setPerformanceData] = useState({
    minutes_played: '',
    position_played: '',
    rating: '',
    coach_comment: '',
    strengths: '',
    improvements: ''
  });

  useEffect(() => {
    fetchMatches();
    fetchMyAthletes();
    axios.get(`${API}/tags/map`).then(r => setTagMap(r.data || {})).catch(() => {});
  }, []);

  const fetchMatches = async () => {
    try {
      const response = await axios.get(`${API}/matches`);
      setMatches(response.data);
    } catch (error) {
      toast.error('Failed to fetch matches');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyAthletes = async () => {
    try {
      const response = await axios.get(`${API}/athletes`);
      setAthletes(response.data);
    } catch (error) {
      console.error('Failed to fetch athletes:', error);
    }
  };

  const handleCreateMatch = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/matches`, newMatch);
      toast.success('Match created successfully');
      setCreateDialogOpen(false);
      setNewMatch({ date: '', opponent: '', competition: '', location: '', result: '', target_type: 'all', target_squads: [], target_athletes: [] });
      fetchMatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create match');
    }
  };

  const openPerformanceDialog = (match, athlete) => {
    setSelectedMatch(match);
    setSelectedAthlete(athlete);
    setPerformanceData({
      minutes_played: '',
      position_played: '',
      rating: '',
      coach_comment: '',
      strengths: '',
      improvements: ''
    });
    setPerformanceDialogOpen(true);
  };

  const handleSubmitPerformance = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        athlete_id: selectedAthlete.id,
        minutes_played: performanceData.minutes_played ? parseInt(performanceData.minutes_played) : null,
        position_played: performanceData.position_played || null,
        rating: performanceData.rating ? parseFloat(performanceData.rating) : null,
        coach_comment: performanceData.coach_comment || null,
        strengths: performanceData.strengths || null,
        improvements: performanceData.improvements || null
      };

      await axios.post(`${API}/matches/${selectedMatch.id}/performance`, payload);
      toast.success('Performance report submitted successfully');
      setPerformanceDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit performance report');
    }
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Match Management
            </h2>
            <p className="text-gray-600 dark:text-gray-500 mt-1">Record and manage match information</p>
          </div>

          <div className="flex items-center gap-2">
          <Button
            onClick={() => setWizardOpen(true)}
            className="bg-pfap-green hover:bg-pfap-green/90 text-white"
            data-testid="open-post-match-wizard"
          >
            Post-Match Workflow
          </Button>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="create-match-button">
                + Create Match
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="create-match-dialog">
              <DialogHeader>
                <DialogTitle>Create Match</DialogTitle>
                <DialogDescription>Add a new match to the schedule</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateMatch}>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="date">Date</Label>
                      <Input
                        id="date"
                        type="date"
                        value={newMatch.date}
                        onChange={(e) => setNewMatch({...newMatch, date: e.target.value})}
                        required
                        data-testid="match-date-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="opponent">Opponent</Label>
                      <Input
                        id="opponent"
                        value={newMatch.opponent}
                        onChange={(e) => setNewMatch({...newMatch, opponent: e.target.value})}
                        placeholder="e.g., FC Porto"
                        required
                        data-testid="match-opponent-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="competition">Competition</Label>
                    <Input
                      id="competition"
                      value={newMatch.competition}
                      onChange={(e) => setNewMatch({...newMatch, competition: e.target.value})}
                      placeholder="e.g., League, Cup"
                      required
                      data-testid="match-competition-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      value={newMatch.location}
                      onChange={(e) => setNewMatch({...newMatch, location: e.target.value})}
                      placeholder="e.g., Home, Away"
                      required
                      data-testid="match-location-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="result">Result (optional)</Label>
                    <Input
                      id="result"
                      value={newMatch.result}
                      onChange={(e) => setNewMatch({...newMatch, result: e.target.value})}
                      placeholder="e.g., 3-1 Win"
                      data-testid="match-result-input"
                    />
                  </div>
                  <TargetAudienceSelector
                    athletes={athletes}
                    value={{ target_type: newMatch.target_type, target_squads: newMatch.target_squads, target_athletes: newMatch.target_athletes }}
                    onChange={(val) => setNewMatch({...newMatch, target_type: val.target_type, target_squads: val.target_squads, target_athletes: val.target_athletes})}
                    label="Squad / Target Audience"
                    compact
                    data-testid="match-target-audience"
                  />
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="submit-create-match">Create Match</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          </div>
        ) : matches.length === 0 ? (
          <Card data-testid="no-matches-message">
            <CardContent className="py-12 text-center">
              <p className="text-gray-500 dark:text-gray-500">No matches yet. Create your first match to get started.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="matches-list">
            {matches.map((match) => (
              <Card key={match.id} className="hover:shadow-md transition-shadow" data-testid={`match-card-${match.id}`}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-xl" data-testid={`match-title-${match.id}`}>
                        vs {match.opponent}
                      </CardTitle>
                      <CardDescription>
                        {new Date(match.date).toLocaleDateString()} • {match.competition} • {match.location}
                      </CardDescription>
                      {match.result && (
                        <Badge className="mt-2 bg-emerald-100 text-emerald-800 border-emerald-200">
                          {match.result}
                        </Badge>
                      )}
                      {match.target_type === 'custom' && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(match.target_squads || []).map(s => (
                            <Badge key={s} variant="outline" className="text-[10px] px-1.5 py-0">{s}</Badge>
                          ))}
                          {(match.target_athletes || []).length > 0 && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                              {match.target_athletes.length} athlete{match.target_athletes.length > 1 ? 's' : ''}
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Add Performance Report:</p>
                    <div className="flex gap-3 items-end">
                      <div className="flex-1 max-w-sm">
                        <SearchableAthleteSelect
                          athletes={match.target_type === 'custom' 
                            ? (() => {
                                const tagIds = match.target_tag_ids || [];
                                const tagSet = new Set(tagIds);
                                return athletes.filter(a => 
                                  (match.target_squads || []).includes(a.squad) || 
                                  (match.target_athletes || []).includes(a.id) ||
                                  (tagIds.length > 0 && (tagMap[a.id] || []).some(t => tagSet.has(t)))
                                );
                              })()
                            : athletes
                          }
                          value=""
                          onChange={(athleteId) => {
                            const athlete = athletes.find(a => a.id === athleteId);
                            if (athlete) openPerformanceDialog(match, athlete);
                          }}
                          placeholder="Search & select athlete..."
                          data-testid={`performance-select-${match.id}`}
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Performance Report Dialog */}
        <Dialog open={performanceDialogOpen} onOpenChange={setPerformanceDialogOpen}>
          <DialogContent className="max-w-2xl" data-testid="performance-dialog">
            <DialogHeader>
              <DialogTitle>Performance Report</DialogTitle>
              <DialogDescription>
                {selectedAthlete?.full_name} - {selectedMatch?.opponent}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmitPerformance}>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="minutes_played">Minutes Played</Label>
                    <Input
                      id="minutes_played"
                      type="number"
                      min="0"
                      max="120"
                      value={performanceData.minutes_played}
                      onChange={(e) => setPerformanceData({...performanceData, minutes_played: e.target.value})}
                      data-testid="minutes-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="position_played">Position Played</Label>
                    <Input
                      id="position_played"
                      value={performanceData.position_played}
                      onChange={(e) => setPerformanceData({...performanceData, position_played: e.target.value})}
                      placeholder="e.g., Forward"
                      data-testid="position-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="rating">Rating (1-10)</Label>
                  <Input
                    id="rating"
                    type="number"
                    min="1"
                    max="10"
                    step="0.5"
                    value={performanceData.rating}
                    onChange={(e) => setPerformanceData({...performanceData, rating: e.target.value})}
                    data-testid="rating-input"
                  />
                </div>
                <div>
                  <Label htmlFor="coach_comment">Coach Comment</Label>
                  <Textarea
                    id="coach_comment"
                    value={performanceData.coach_comment}
                    onChange={(e) => setPerformanceData({...performanceData, coach_comment: e.target.value})}
                    placeholder="Overall performance notes..."
                    data-testid="comment-input"
                  />
                </div>
                <div>
                  <Label htmlFor="strengths">Strengths</Label>
                  <Textarea
                    id="strengths"
                    value={performanceData.strengths}
                    onChange={(e) => setPerformanceData({...performanceData, strengths: e.target.value})}
                    placeholder="What went well..."
                    data-testid="strengths-input"
                  />
                </div>
                <div>
                  <Label htmlFor="improvements">Areas for Improvement</Label>
                  <Textarea
                    id="improvements"
                    value={performanceData.improvements}
                    onChange={(e) => setPerformanceData({...performanceData, improvements: e.target.value})}
                    placeholder="What to work on..."
                    data-testid="improvements-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="submit-performance">Submit Report</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <PostMatchWizard
          open={wizardOpen}
          onOpenChange={setWizardOpen}
          athletes={athletes}
          onCompleted={() => fetchMatches()}
        />
      </div>
    </Layout>
  );
};

export default StaffMatches;
